import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createCohortInvite,
  listCohortInvites,
  listCohortRequests,
  listCohortStudentCandidates,
  matchCohortRequest,
  rejectCohortRequest,
  revokeCohortInvite,
} from '../api/cohort';
import { adminListUsers } from '../api/admin';
import { getStoredUser, parseJsonSafe } from '../api/client';

function extractErrorMessage(data, fallback) {
  return (
    (typeof data?.detail === 'string' && data.detail) ||
    (typeof data?.message === 'string' && data.message) ||
    fallback
  );
}

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function CohortManagementPage() {
  const role = getStoredUser()?.role;
  const isAdmin = role === 'ADMIN';
  const [invites, setInvites] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [selectedTutorId, setSelectedTutorId] = useState('');
  const [candidatesByRequestId, setCandidatesByRequestId] = useState({});
  const [selectedCandidateByRequestId, setSelectedCandidateByRequestId] = useState({});
  const [generatedLink, setGeneratedLink] = useState('');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [busyAction, setBusyAction] = useState('');

  const load = useCallback(async () => {
    setErrorMessage('');
    try {
      const [inviteResponse, requestResponse] = await Promise.all([
        listCohortInvites(),
        listCohortRequests(),
      ]);
      const inviteData = await parseJsonSafe(inviteResponse);
      const requestData = await parseJsonSafe(requestResponse);
      if (!inviteResponse.ok) throw new Error(extractErrorMessage(inviteData, 'Could not load cohort links.'));
      if (!requestResponse.ok) throw new Error(extractErrorMessage(requestData, 'Could not load cohort requests.'));
      setInvites(Array.isArray(inviteData) ? inviteData : []);
      setRequests(Array.isArray(requestData) ? requestData : []);
    } catch (error) {
      setErrorMessage(error.message || 'Could not load cohort sign-up data.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let active = true;
    adminListUsers()
      .then(async (response) => {
        const data = await parseJsonSafe(response);
        if (!response.ok) throw new Error(extractErrorMessage(data, 'Could not load tutors.'));
        if (active) setTutors((Array.isArray(data) ? data : []).filter((user) => user.role === 'TUTOR' && user.is_active !== false));
      })
      .catch((error) => {
        if (active) setErrorMessage(error.message || 'Could not load tutors.');
      });
    return () => { active = false; };
  }, [isAdmin]);

  const handleCreateInvite = async () => {
    if (isAdmin && !selectedTutorId) {
      setErrorMessage('Select a tutor before creating a cohort link.');
      return;
    }
    setBusyAction('create-invite');
    setMessage('');
    setErrorMessage('');
    try {
      const response = await createCohortInvite(isAdmin ? selectedTutorId : null);
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(extractErrorMessage(data, 'Could not create the cohort link.'));
      const link = `${window.location.origin}/cohort-signup?code=${encodeURIComponent(data.code || '')}`;
      setGeneratedLink(link);
      setMessage('New cohort link created. Copy it now; the code is shown only in this response.');
      await load();
    } catch (error) {
      setErrorMessage(error.message || 'Could not create the cohort link.');
    } finally {
      setBusyAction('');
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      setMessage('Cohort link copied.');
    } catch (_error) {
      setMessage('Copy was unavailable; select the link and copy it manually.');
    }
  };

  const handleRevoke = async (inviteId) => {
    setBusyAction(`revoke-${inviteId}`);
    setMessage('');
    try {
      const response = await revokeCohortInvite(inviteId);
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(extractErrorMessage(data, 'Could not revoke the cohort link.'));
      setMessage('Cohort link revoked.');
      await load();
    } catch (error) {
      setErrorMessage(error.message || 'Could not revoke the cohort link.');
    } finally {
      setBusyAction('');
    }
  };

  const findCandidates = async (requestId) => {
    setBusyAction(`find-${requestId}`);
    setErrorMessage('');
    try {
      const response = await listCohortStudentCandidates(requestId);
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(extractErrorMessage(data, 'Could not find matching student accounts.'));
      setCandidatesByRequestId((current) => ({ ...current, [requestId]: Array.isArray(data) ? data : [] }));
    } catch (error) {
      setErrorMessage(error.message || 'Could not find matching student accounts.');
    } finally {
      setBusyAction('');
    }
  };

  const handleMatch = async (requestId) => {
    const studentUserId = selectedCandidateByRequestId[requestId];
    if (!studentUserId) {
      setErrorMessage('Find and select the exact existing student account first.');
      return;
    }
    setBusyAction(`match-${requestId}`);
    setMessage('');
    try {
      const response = await matchCohortRequest(requestId, studentUserId);
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(extractErrorMessage(data, 'Could not match the cohort request.'));
      setMessage('Matched. The normal student and guardian approval steps are now required.');
      await load();
    } catch (error) {
      setErrorMessage(error.message || 'Could not match the cohort request.');
    } finally {
      setBusyAction('');
    }
  };

  const handleReject = async (requestId) => {
    setBusyAction(`reject-${requestId}`);
    setMessage('');
    try {
      const response = await rejectCohortRequest(requestId);
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(extractErrorMessage(data, 'Could not reject the cohort request.'));
      setMessage('Cohort request rejected.');
      await load();
    } catch (error) {
      setErrorMessage(error.message || 'Could not reject the cohort request.');
    } finally {
      setBusyAction('');
    }
  };

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <div>
          <p className="eyebrow">{isAdmin ? 'Admin workspace' : 'Tutor workspace'}</p>
          <h1>Cohort sign-up</h1>
          <p>Create a private sign-up link, then review requests and select the exact existing student account.</p>
        </div>
      </section>

      {message ? <p role="status" className="notice notice-info">{message}</p> : null}
      {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}

      <section className="card page-stack">
        <div className="section-header">
          <div><p className="eyebrow">Invite link</p><h2>Create or rotate</h2></div>
        </div>
        {isAdmin ? (
          <div className="stack-xs">
            <label htmlFor="cohort-tutor-select">Tutor</label>
            <select id="cohort-tutor-select" value={selectedTutorId} onChange={(event) => setSelectedTutorId(event.target.value)}>
              <option value="">Select a tutor</option>
              {tutors.map((tutor) => <option key={tutor.id} value={tutor.id}>{tutor.display_name || tutor.email} ({tutor.email})</option>)}
            </select>
          </div>
        ) : null}
        <p className="muted-text">Creating a link revokes the previous active link for this tutor. Links expire after the configured beta lifetime.</p>
        <button type="button" className="btn btn-primary" onClick={handleCreateInvite} disabled={busyAction === 'create-invite'}>
          {busyAction === 'create-invite' ? 'Creating...' : 'Create new sign-up link'}
        </button>
        {generatedLink ? (
          <div className="stack-xs">
            <label htmlFor="generated-cohort-link">New sign-up link</label>
            <input id="generated-cohort-link" value={generatedLink} readOnly onFocus={(event) => event.target.select()} />
            <button type="button" className="btn btn-secondary" onClick={handleCopyLink}>Copy link</button>
          </div>
        ) : null}
        {invites.length ? (
          <ul className="data-list">
            {invites.map((invite) => (
              <li key={invite.id} className="data-list-item">
                <strong>Link {invite.id}</strong>
                <p className="muted-text">Expires: {formatDate(invite.expires_at)}{invite.revoked_at ? ` · Revoked: ${formatDate(invite.revoked_at)}` : ''}</p>
                {!invite.revoked_at ? <button type="button" className="btn btn-secondary" disabled={busyAction === `revoke-${invite.id}`} onClick={() => handleRevoke(invite.id)}>Revoke</button> : null}
              </li>
            ))}
          </ul>
        ) : <p className="muted-text">No cohort links yet.</p>}
      </section>

      <section className="card page-stack">
        <div className="section-header">
          <div><p className="eyebrow">Review queue</p><h2>Sign-up requests</h2></div>
        </div>
        {requests.length === 0 ? <p className="muted-text">No sign-up requests.</p> : (
          <ul className="data-list">
            {requests.map((request) => {
              const candidates = candidatesByRequestId[request.id];
              const selectedCandidate = selectedCandidateByRequestId[request.id] || '';
              return (
                <li key={request.id} className="data-list-item">
                  <div className="list-item-header">
                    <div>
                      <strong>{request.first_name} {request.last_name}</strong>
                      <p className="muted-text">{request.student_email} · DOB {request.date_of_birth} · guardian {request.guardian_email}</p>
                    </div>
                    <span className="muted-text">{request.status.toLowerCase()}</span>
                  </div>
                  {request.status === 'PENDING' ? (
                    <div className="page-stack">
                      {!candidates ? <button type="button" className="btn btn-secondary" disabled={busyAction === `find-${request.id}`} onClick={() => findCandidates(request.id)}>{busyAction === `find-${request.id}` ? 'Searching...' : 'Find existing student account'}</button> : null}
                      {candidates && candidates.length === 0 ? <p className="notice notice-warn">No active student account matches this email. <Link to="/admin/users/create">Create or verify the account</Link>, then search again.</p> : null}
                      {candidates && candidates.length ? (
                        <>
                          <div className="stack-xs">
                            <label htmlFor={`cohort-candidate-${request.id}`}>Exact student account</label>
                            <select id={`cohort-candidate-${request.id}`} value={selectedCandidate} onChange={(event) => setSelectedCandidateByRequestId((current) => ({ ...current, [request.id]: event.target.value }))}>
                              <option value="">Select an account</option>
                              {candidates.map((candidate) => <option key={candidate.user_id} value={candidate.user_id}>{candidate.display_name} · {candidate.email} · {candidate.account_state}</option>)}
                            </select>
                          </div>
                          <div>
                            <button type="button" className="btn btn-primary" disabled={!selectedCandidate || busyAction === `match-${request.id}`} onClick={() => handleMatch(request.id)}>{busyAction === `match-${request.id}` ? 'Matching...' : 'Match and request approval'}</button>{' '}
                            <button type="button" className="btn btn-secondary" disabled={busyAction === `reject-${request.id}`} onClick={() => handleReject(request.id)}>Reject</button>
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

export default CohortManagementPage;
